

import { FocusMonitor } from '@angular/cdk/a11y';
import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MwcFormStatusService } from 'mwc/core/form';
import { BooleanInput, MwcSafeAny, OnChangeType, OnTouchedType } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import { MwcCheckboxWrapperComponent } from './checkbox-wrapper.component';

@Component({
  selector: '[mwc-checkbox]',
  exportAs: 'mwcCheckbox',
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <span
      class="mwc-checkbox"
      [class.mwc-checkbox-checked]="mwcChecked && !mwcIndeterminate"
      [class.mwc-checkbox-disabled]="mwcDisabled"
      [class.mwc-checkbox-indeterminate]="mwcIndeterminate"
    >
      <input
        #inputElement
        type="checkbox"
        class="mwc-checkbox-input"
        [attr.autofocus]="mwcAutoFocus ? 'autofocus' : null"
        [attr.id]="mwcId"
        [checked]="mwcChecked"
        [ngModel]="mwcChecked"
        [disabled]="mwcDisabled"
        (ngModelChange)="innerCheckedChange($event)"
      />
      <span class="mwc-checkbox-inner"></span>
    </span>
    <span><ng-content></ng-content></span>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MwcCheckboxComponent),
      multi: true
    }
  ],
  host: {
    class: 'mwc-checkbox-wrapper',
    '[class.mwc-checkbox-small]': `mwcSize === 'small'`,
    '[class.mwc-checkbox-wrapper-in-form-item]': '!!mwcFormStatusService',
    '[class.mwc-checkbox-wrapper-checked]': 'mwcChecked',
    '[class.mwc-checkbox-rtl]': `dir === 'rtl'`
  }
})
export class MwcCheckboxComponent implements OnInit, ControlValueAccessor, OnDestroy, AfterViewInit {
  static ngAcceptInputType_mwcAutoFocus: BooleanInput;
  static ngAcceptInputType_mwcDisabled: BooleanInput;
  static ngAcceptInputType_mwcIndeterminate: BooleanInput;
  static ngAcceptInputType_mwcChecked: BooleanInput;

  dir: Direction = 'ltr';
  private destroy$ = new Subject<void>();
  private isMwcDisableFirstChange: boolean = true;

  onChange: OnChangeType = () => {};
  onTouched: OnTouchedType = () => {};
  @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
  @Output() readonly mwcCheckedChange = new EventEmitter<boolean>();
  @Input() mwcValue: MwcSafeAny | null = null;
  @Input() @InputBoolean() mwcAutoFocus = false;
  @Input() @InputBoolean() mwcDisabled = false;
  @Input() @InputBoolean() mwcIndeterminate = false;
  @Input() @InputBoolean() mwcChecked = false;
  @Input() mwcId: string | null = null;
  @Input() mwcSize: 'small' | 'default' = 'default';

  innerCheckedChange(checked: boolean): void {
    if (!this.mwcDisabled) {
      this.mwcChecked = checked;
      this.onChange(this.mwcChecked);
      this.mwcCheckedChange.emit(this.mwcChecked);
      if (this.mwcCheckboxWrapperComponent) {
        this.mwcCheckboxWrapperComponent.onChange();
      }
    }
  }

  writeValue(value: boolean): void {
    this.mwcChecked = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: OnChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedType): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.mwcDisabled = (this.isMwcDisableFirstChange && this.mwcDisabled) || disabled;
    this.isMwcDisableFirstChange = false;
    this.cdr.markForCheck();
  }

  focus(): void {
    this.focusMonitor.focusVia(this.inputElement, 'keyboard');
  }

  blur(): void {
    this.inputElement.nativeElement.blur();
  }

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() private mwcCheckboxWrapperComponent: MwcCheckboxWrapperComponent,
    private cdr: ChangeDetectorRef,
    private focusMonitor: FocusMonitor,
    @Optional() private directionality: Directionality,
    @Optional() public mwcFormStatusService?: MwcFormStatusService
  ) {}

  ngOnInit(): void {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(focusOrigin => {
        if (!focusOrigin) {
          Promise.resolve().then(() => this.onTouched());
        }
      });
    if (this.mwcCheckboxWrapperComponent) {
      this.mwcCheckboxWrapperComponent.addCheckbox(this);
    }

    this.directionality.change.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.detectChanges();
    });

    this.dir = this.directionality.value;

    this.ngZone.runOutsideAngular(() => {
      fromEvent(this.elementRef.nativeElement, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          event.preventDefault();
          this.focus();
          if (this.mwcDisabled) {
            return;
          }
          this.ngZone.run(() => {
            this.innerCheckedChange(!this.mwcChecked);
            this.cdr.markForCheck();
          });
        });

      fromEvent(this.inputElement.nativeElement, 'click')
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => event.stopPropagation());
    });
  }

  ngAfterViewInit(): void {
    if (this.mwcAutoFocus) {
      this.focus();
    }
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
    if (this.mwcCheckboxWrapperComponent) {
      this.mwcCheckboxWrapperComponent.removeCheckbox(this);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
